import {SiteEntity} from "@ots-frontend/ots-data";
import {UseFormReturnType} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Divider, Grid, Modal, ModalProps, NumberInput, Textarea} from "@mantine/core";
import {useCallback} from "react";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";
import {useLocationStatusFacetForm} from "./useLocationStatusFacetForm";
import {
  DateInput,
  RefServiceModeSelect,
  RefServiceStatusFacetSelect,
  RefServiceStatusSelect,
  ServiceTypeSelect,
  SiteSelect
} from "@ots-frontend/ots-ui";

export interface LocationStatusFacetFormProps {
  options?: LocationStatusFacetFormOptions;
  defaultValues?: Partial<LocationStatusFacetFormValues>;
  onSubmit?: (values: LocationStatusFacetFormValues) => void;
  submitting?: boolean;
}

export interface LocationStatusFacetFormOptions {
  regionId?: string;
  siteSelectDisabled?: boolean
}

export interface LocationStatusFacetFormValues {
  site: SiteEntity | string
  serviceType: string;
  statusFacet: string;
  status: string;
  asOfDate: Date;
  serviceMode: string;
  confidencePct: number;
  note?: string
}

export interface LocationStatusFacetFormDefaultValues extends Partial<LocationStatusFacetFormValues> {

}

export interface LocationStatusFacetFormRawValues {
  site?: string
  serviceType?: string
  statusFacet?: string
  status?: string
  asOfDate?: Date;
  serviceMode?: string
  confidencePct?: number;
  note?: string
}

export type LocationStatusFacetFormType = UseFormReturnType<LocationStatusFacetFormRawValues, (values: LocationStatusFacetFormRawValues) => LocationStatusFacetFormRawValues>

export const LocationStatusFacetForm = (props: LocationStatusFacetFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const {regionId} = useApp();

  const {t} = useTranslation(["common"]);

  const form = useLocationStatusFacetForm({
    defaultValues: defaultValues,
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        site: values.site!,
        serviceType: values.serviceType!,
        statusFacet: values.statusFacet!,
        status: values.status!,
        asOfDate: values.asOfDate!,
        serviceMode: values.serviceMode!,
        confidencePct: values.confidencePct!,
        note: values.note,
      });
    }
  }, [form, onSubmit]);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <Grid grow gutter="xs">
        <Grid.Col span={12}>
          <SiteSelect
            size="xs"
            label={t("common.entity.locationstatusfacet.form.field.site", "Site")}
            placeholder={t<string>("common.entity.locationstatusfacet.form.field.site.placeholder", "Site")}
            withAsterisk
            required
            clearable
            disabled={options?.siteSelectDisabled}
            selector={{
              regionId: options?.regionId,
            }}
            {...form.getInputProps("site")} />
        </Grid.Col>
        <Grid.Col span={12}>
          <DateInput
            size="xs"
            valueFormat="MM/DD/YYYY HH:mm:ss"
            label={t("common.entity.locationstatusfacet.form.field.date", "Date")}
            placeholder={t<string>("common.entity.locationstatusfacet.form.field.date.placeholder", "Date")}
            withAsterisk
            required
            {...form.getInputProps("asOfDate")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={6}>
          <ServiceTypeSelect
            size="xs"
            label={t("common.entity.locationstatusfacet.form.field.servicetype", "Service Type")}
            placeholder={t<string>("common.entity.locationstatusfacet.form.field.servicetype.placeholder", "Service Type")}
            withAsterisk
            required
            clearable
            selector={{
              regionId: options?.regionId,
            }}
            {...form.getInputProps("serviceType")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <RefServiceStatusFacetSelect
            size="xs"
            label={t("common.entity.locationstatusfacet.form.field.statusfacet", "Status Facet")}
            placeholder={t<string>("common.entity.locationstatusfacet.form.field.freighttype.placeholder", "Status Facet")}
            withAsterisk
            required
            clearable
            {...form.getInputProps("statusFacet")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <RefServiceStatusSelect
            size="xs"
            label={t("common.entity.locationstatusfacet.form.field.status", "Status")}
            placeholder={t<string>("common.entity.locationstatusfacet.form.field.status.placeholder", "Status")}
            withAsterisk
            required
            clearable
            {...form.getInputProps("status")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <RefServiceModeSelect
            size="xs"
            label={t("common.entity.locationstatusfacet.form.field.servicemode", "Mode")}
            placeholder={t<string>("common.entity.locationstatusfacet.form.field.servicemode.placeholder", "Mode")}
            withAsterisk
            required
            clearable
            {...form.getInputProps("serviceMode")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={6}>
          <NumberInput
            size="xs"
            label={t("common.entity.locationstatusfacet.form.field.confidence", "Confidence (%)")}
            placeholder={t<string>("common.entity.locationstatusfacet.form.field.confidence.placeholder", "Confidence (%)")}
            precision={0}
            min={0}
            max={100}
            step={1}
            hideControls
            {...form.getInputProps("confidencePct")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={12}>
          <Textarea
            size="xs"
            label={t("common.entity.locationstatusfacet.form.field.note", "Note")}
            placeholder={t<string>("common.entity.locationstatusfacet.form.field.note.placeholder", "Note")}
            {...form.getInputProps("note")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Button type="submit" loading={submitting} fullWidth mt="xl">
        {t("common.action.submit", {ns: "common"})}
      </Button>
    </form>
  );
};

interface LocationStatusFacetFormModalProps extends LocationStatusFacetFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const LocationStatusFacetFormModal = (props: LocationStatusFacetFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    submitting,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <LocationStatusFacetForm defaultValues={defaultValues} onSubmit={onSubmit} submitting={submitting}/>
    </Modal>
  );
};
